/**
 * error-context.ts
 *
 * `serializeError` turns any thrown value into a flat, PostHog-safe property
 * bag. The goal is debuggable `$exception` events: a Slack alert that
 * contains the stack, the error_cause chain, and the narrowest business
 * context should let an on-call engineer form a real hypothesis without
 * clicking through to replay or CloudWatch.
 *
 * Contract:
 *  - Never throws. Errors inside the serializer become `{ error_name: "UnknownError", error_message: ... }`.
 *  - `error_stack` is trimmed to STACK_LIMIT_BYTES (PostHog rejects overly
 *    large property values) — the trailing bytes are preferred because the
 *    innermost frames matter more than the outer Lambda boilerplate.
 *  - `error.cause` is walked up to CAUSE_DEPTH_LIMIT and joined so a wrapped
 *    error chain stays readable in Slack.
 *  - `context` is merged LAST so callers can override everything, including
 *    error_message when a sanitized message is needed.
 *  - Cyclic values in `context` are replaced with "<unserializable>".
 *    Functions and symbols are dropped entirely (PostHog drops them anyway).
 */
export interface SerializedError {
  error_name: string;
  error_message: string;
  error_stack?: string;
  error_cause?: string;
  /**
   * Next.js attaches a `digest` to errors thrown in Server Components / Server
   * Actions and redacts the real message in production ("An error occurred in
   * the Server Components render… A digest property is included…"). The digest
   * is the key that ties the client-side redacted error to the full server-side
   * error in CloudWatch, so capturing it makes those otherwise-opaque failures
   * debuggable without a redeploy. See BRE-4752.
   */
  error_digest?: string;
  [key: string]: unknown;
}

const STACK_LIMIT_BYTES = 4096;
const CAUSE_DEPTH_LIMIT = 3;

export function serializeError(
  error: unknown,
  context?: Record<string, unknown>,
): SerializedError {
  const base = safeExtract(error);
  const sanitizedContext = sanitizeContext(context);
  return { ...base, ...sanitizedContext };
}

function safeExtract(error: unknown): SerializedError {
  try {
    if (error instanceof Error) {
      const result: SerializedError = {
        error_name: typeof error.name === "string" ? error.name : "Error",
        error_message: typeof error.message === "string" ? error.message : String(error),
      };
      const digest = (error as { digest?: unknown }).digest;
      if (typeof digest === "string" && digest.length > 0) {
        result.error_digest = digest;
      }
      if (typeof error.stack === "string" && error.stack.length > 0) {
        result.error_stack = trimStack(error.stack);
      }
      const cause = collectCauseChain(error);
      if (cause.length > 0) {
        result.error_cause = cause.join(" <- ");
      }
      return result;
    }

    if (error === null || error === undefined) {
      return { error_name: "UnknownError", error_message: String(error) };
    }

    if (typeof error === "string") {
      return { error_name: "StringError", error_message: error };
    }

    if (typeof error === "object") {
      const record = error as Record<string, unknown>;
      const name = typeof record.name === "string" ? record.name : "UnknownError";
      const message =
        typeof record.message === "string"
          ? record.message
          : safeStringify(error) ?? String(error);
      const result: SerializedError = { error_name: name, error_message: message };
      if (typeof record.digest === "string" && record.digest.length > 0) {
        result.error_digest = record.digest;
      }
      if (typeof record.stack === "string" && record.stack.length > 0) {
        result.error_stack = trimStack(record.stack);
      }
      return result;
    }

    return { error_name: "UnknownError", error_message: String(error) };
  } catch {
    // Absolute worst case: extraction itself threw. Fall back to a string form
    // so observability still records *something* about the error.
    return {
      error_name: "UnknownError",
      error_message: tryString(error),
    };
  }
}

function collectCauseChain(error: Error): string[] {
  const chain: string[] = [];
  const seen = new Set<unknown>();
  seen.add(error);

  let current: unknown = (error as Error & { cause?: unknown }).cause;
  let depth = 0;
  while (current !== undefined && depth < CAUSE_DEPTH_LIMIT) {
    if (seen.has(current)) break;
    seen.add(current);

    if (current instanceof Error) {
      chain.push(
        `${typeof current.name === "string" ? current.name : "Error"}: ${current.message}`,
      );
      current = (current as Error & { cause?: unknown }).cause;
    } else if (typeof current === "string") {
      chain.push(current);
      current = undefined;
    } else if (current && typeof current === "object") {
      const record = current as { message?: unknown; name?: unknown; cause?: unknown };
      const name = typeof record.name === "string" ? record.name : "Cause";
      const message =
        typeof record.message === "string" ? record.message : safeStringify(current) ?? "<object>";
      chain.push(`${name}: ${message}`);
      current = record.cause;
    } else {
      chain.push(String(current));
      current = undefined;
    }
    depth++;
  }
  return chain;
}

function trimStack(stack: string): string {
  if (stack.length <= STACK_LIMIT_BYTES) return stack;
  // V8 stacks are ordered innermost-first: the first line is the error
  // header, the next lines are the frames closest to the throw site. The
  // Node/Lambda bootstrap is at the bottom, which is the part we can drop
  // without losing the root cause. Keep the leading STACK_LIMIT_BYTES.
  const trimmed = stack.slice(0, STACK_LIMIT_BYTES);
  return `${trimmed}\n...<truncated>`;
}

function sanitizeContext(
  context: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!context) return {};
  const seen = new WeakSet<object>();
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(context)) {
    const value = context[key];
    const sanitized = sanitizeValue(value, seen);
    if (sanitized !== DROPPED) result[key] = sanitized;
  }
  return result;
}

const DROPPED = Symbol("dropped");

function sanitizeValue(
  value: unknown,
  seen: WeakSet<object>,
): unknown | typeof DROPPED {
  if (value === null || value === undefined) return value;
  const t = typeof value;
  if (t === "string" || t === "number" || t === "boolean" || t === "bigint") return value;
  if (t === "function" || t === "symbol") return DROPPED;

  if (Array.isArray(value)) {
    if (seen.has(value)) return "<unserializable>";
    seen.add(value);
    const out: unknown[] = [];
    for (const item of value) {
      const sanitized = sanitizeValue(item, seen);
      out.push(sanitized === DROPPED ? null : sanitized);
    }
    return out;
  }

  if (t === "object") {
    const obj = value as Record<string, unknown>;
    if (seen.has(obj)) return "<unserializable>";
    seen.add(obj);
    // Preserve Error shape in context (caller may pass a secondary error as context).
    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        ...(value.stack ? { stack: trimStack(value.stack) } : {}),
      };
    }
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      const sanitized = sanitizeValue(obj[key], seen);
      if (sanitized !== DROPPED) out[key] = sanitized;
    }
    return out;
  }

  return DROPPED;
}

function safeStringify(value: unknown): string | undefined {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

function tryString(value: unknown): string {
  try {
    return String(value);
  } catch {
    return "<unserializable>";
  }
}
